import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, CardActions, Button, TextField,
  Tabs, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, Alert, Snackbar, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, IconButton, Avatar,
  List, ListItem, ListItemText, ListItemAvatar, Divider, useTheme, useMediaQuery,
  Checkbox
} from '@mui/material';
// MUI v7 Grid dropped item/xs props — legacy API keeps Documents layouts full-width on mobile
import Grid from '@mui/material/GridLegacy';
import {
  Description as DocumentIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Work as WorkIcon,
  Person as PersonIcon,
  Home as HomeIcon,
  CheckCircle as CheckCircleIcon,
  AttachFile as AttachIcon,
  Security as SecurityIcon,
  Folder as FolderIcon,
  CloudUpload as CloudUploadIcon,
  CloudDownload as CloudDownloadIcon,
  Assignment as AssignmentIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  LibraryBooks as LibraryBooksIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  CloudDone as CloudDoneIcon
} from '@mui/icons-material';
import DrawIcon from '@mui/icons-material/Gesture';
import api from '../api';
import {
  normalizeSignatureRequest,
  pendingSignerEmails
} from '../utils/eSignature';
import PdfFieldPlacer from './esign/PdfFieldPlacer';
import { toApiEsignField } from '../utils/esignFields';
import { isGoogleWorkspaceEnabled } from '../config/featureFlags';

/** Matches backend documentsRegistry ATTACH_WARN_MESSAGE (DOC-02/DOC-03). */
const ATTACH_WARN_MESSAGE =
  'Attach a property or tenant so this file shows on that record and in the portal when shared.';

function driveUrlFor(doc) {
  if (doc?.googleDriveUrl) return doc.googleDriveUrl;
  if (doc?.googleDriveId) {
    return `https://drive.google.com/file/d/${doc.googleDriveId}/view`;
  }
  return null;
}

function isDocAttached(doc) {
  return Boolean(doc?.propertyId || doc?.tenantId || doc?.leaseId);
}

function isDocDriveSynced(doc) {
  return Boolean(doc?.syncedWithGoogleDrive || doc?.googleDriveId);
}

/**
 * @param {{ focus?: { entityType: 'property' | 'tenant' | 'lease', entityId: string, key?: number } }} props
 *   focus: opens the hub pre-filtered to one record (e.g. from a lease's Files strip).
 */
const DocumentManagement = ({ focus = null }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const googleEnabled = isGoogleWorkspaceEnabled();
  const uploadFileInputRef = useRef(null);
  const pickingUploadFileRef = useRef(false);
  
  const [activeTab, setActiveTab] = useState(0);
  const [documents, setDocuments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [attachConfirmOpen, setAttachConfirmOpen] = useState(false);
  
  // Dialogs
  const [uploadDialog, setUploadDialog] = useState(false);
  const [templateDialog, setTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [viewDialog, setViewDialog] = useState(false);
  const [signDialog, setSignDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  
  // Form states
  const [uploadData, setUploadData] = useState({
    title: '',
    description: '',
    category: '',
    propertyId: '',
    tenantId: '',
    tags: [],
    file: null,
    expiryDate: ''
  });

  const [editData, setEditData] = useState({
    id: '',
    title: '',
    description: '',
    category: '',
    tags: [],
    status: '',
    expiryDate: '',
    propertyId: '',
    tenantId: ''
  });

  // Dedicated Create Google Doc dialog (DOC-QA) — was broken: button disabled until sync folder set
  const [googleDocDialog, setGoogleDocDialog] = useState(false);
  const [googleDocData, setGoogleDocData] = useState({
    title: 'Property Management Document',
    propertyId: '',
    tenantId: '',
    folderId: ''
  });
  const [googleDocAttachConfirmOpen, setGoogleDocAttachConfirmOpen] = useState(false);

  const [templateData, setTemplateData] = useState({
    name: '',
    category: '',
    description: '',
    content: ''
  });

  const [signatureData, setSignatureData] = useState({
    documentId: '',
    signerName: '',
    signerEmail: '',
    signature: ''
  });

  // Filters (DOC-03 F3)
  const [filters, setFilters] = useState({
    category: 'all',
    status: 'all',
    dateRange: 'all',
    entityType: 'all',
    entityId: '',
    attachment: 'all',
    drive: 'all',
    search: ''
  });

  const [googleFolders, setGoogleFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('');
  const [syncDialog, setSyncDialog] = useState(false);
  const [signatureRequestDialog, setSignatureRequestDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedDocumentForSync, setSelectedDocumentForSync] = useState(null);
  const [signatureRequestData, setSignatureRequestData] = useState({
    documentId: '',
    signers: [],
    message: '',
    fields: []
  });

  // Enhanced Signature Management States
  const [signatureRequests, setSignatureRequests] = useState([]);
  const [signaturesLoading, setSignaturesLoading] = useState(false);
  const [signatureSearchTerm, setSignatureSearchTerm] = useState('');
  const [signatureStatusFilter, setSignatureStatusFilter] = useState('pending');
  const [selectedSignatureRequest, setSelectedSignatureRequest] = useState(null);
  const [viewSignatureDialog, setViewSignatureDialog] = useState(false);
  const [createdSigningLink, setCreatedSigningLink] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, doc: null });
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [cancelConfirm, setCancelConfirm] = useState({ open: false, request: null });
  const [cancelAllConfirmOpen, setCancelAllConfirmOpen] = useState(false);

  // ZRF-ESIGN-02 BYO field placer
  const [fieldPlacerOpen, setFieldPlacerOpen] = useState(false);
  const [fieldPlacerDoc, setFieldPlacerDoc] = useState(null);

  // Helper: open/download document via authenticated API stream (iOS-safe)
  const openDocumentFile = async (doc, { download = false } = {}) => {
    if (!doc?.id && !doc?.fileUrl) {
      setSnackbar({ open: true, message: 'File not available', severity: 'warning' });
      return;
    }
    try {
      setSnackbar({
        open: true,
        message: download ? 'Preparing download…' : 'Opening preview…',
        severity: 'info'
      });

      let blob;
      let filename = doc.fileName || `${doc.title || 'document'}.pdf`;

      if (doc.id) {
        const res = await api.get(`/api/documents/${doc.id}/file`, {
          responseType: 'blob',
          timeout: 120000
        });
        blob = res.data;
        const cd = res.headers?.['content-disposition'] || '';
        const match = /filename="?([^"]+)"?/i.exec(cd);
        if (match?.[1]) filename = match[1];
      } else {
        const res = await fetch(doc.fileUrl);
        if (!res.ok) throw new Error(`Could not fetch file (${res.status})`);
        blob = await res.blob();
      }

      const objectUrl = URL.createObjectURL(blob);
      if (download) {
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setSnackbar({ open: true, message: 'Download started!', severity: 'success' });
      } else {
        const opened = window.open(objectUrl, '_blank', 'noopener,noreferrer');
        if (!opened) {
          // iOS / popup blocked — navigate same tab
          window.location.href = objectUrl;
        } else {
          setSnackbar({ open: true, message: 'Preview opened', severity: 'success' });
        }
      }
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 120000);
    } catch (error) {
      console.error('❌ Error opening document file:', error);
      // Axios blob mode returns JSON errors as Blob — try to read message
      let apiMsg = error.message || 'Failed to open document';
      try {
        if (error.response?.data instanceof Blob) {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          apiMsg = parsed.error || parsed.details || apiMsg;
        } else {
          apiMsg = error.response?.data?.error || error.response?.data?.details || apiMsg;
        }
      } catch (_) {
        /* keep apiMsg */
      }
      if (doc.fileUrl) {
        const opened = window.open(doc.fileUrl, '_blank', 'noopener,noreferrer');
        if (opened) {
          setSnackbar({ open: true, message: 'Opened stored file link', severity: 'warning' });
          return;
        }
      }
      setSnackbar({ open: true, message: String(apiMsg), severity: 'error' });
    }
  };

  // Legacy helper kept for call sites that only have a URL string
  const downloadFile = async (fileUrl, fileName) => {
    const match = documents.find((d) => d.fileUrl === fileUrl);
    if (match) {
      await openDocumentFile(match, { download: true });
      return;
    }
    try {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileName || 'document.pdf';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setSnackbar({ open: true, message: 'Download started!', severity: 'success' });
    } catch (error) {
      console.error('Error downloading file:', error);
      setSnackbar({ open: true, message: 'Failed to download file', severity: 'error' });
    }
  };

  const [previewBlobUrl, setPreviewBlobUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  useEffect(() => {
    if (!viewDialog || !selectedDocument?.id || !selectedDocument?.fileUrl) {
      setPreviewBlobUrl('');
      setPreviewError('');
      return undefined;
    }

    let cancelled = false;
    let objectUrl = '';

    const load = async () => {
      setPreviewLoading(true);
      setPreviewError('');
      try {
        const res = await api.get(`/api/documents/${selectedDocument.id}/file`, {
          responseType: 'blob',
          timeout: 120000
        });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setPreviewBlobUrl(objectUrl);
      } catch (err) {
        console.error('❌ Inline PDF preview failed:', err);
        if (!cancelled) {
          setPreviewError(err.response?.data?.error || err.message || 'Preview failed');
          setPreviewBlobUrl('');
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [viewDialog, selectedDocument?.id, selectedDocument?.fileUrl]);

  useEffect(() => {
    fetchDocuments();
    if (googleEnabled) fetchGoogleFolders();
    fetchEntityOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!focus?.entityType || !focus?.entityId) return;
    setActiveTab(0);
    setFilters((prev) => ({ ...prev, entityType: focus.entityType, entityId: focus.entityId }));
  }, [focus?.entityType, focus?.entityId, focus?.key]);

  const fetchSignatureRequests = async (docsOverride) => {
    try {
      setSignaturesLoading(true);
      console.log('🔍 Fetching live signature requests...');
      const response = await api.get('/api/e-signature/requests');
      const list = Array.isArray(response.data) ? response.data : [];
      const docs = Array.isArray(docsOverride) ? docsOverride : documents;
      const normalized = list.map((row) => normalizeSignatureRequest(row, docs));
      setSignatureRequests(normalized);
      console.log(`✅ Loaded ${normalized.length} signature requests`);
    } catch (error) {
      console.error('❌ Error fetching signature requests:', error);
      setSignatureRequests([]);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to load signature requests',
        severity: 'error'
      });
    } finally {
      setSignaturesLoading(false);
    }
  };

  const fetchEntityOptions = async () => {
    try {
      const [propsRes, tenantsRes, leasesRes] = await Promise.all([
        api.get('/api/properties'),
        api.get('/api/tenants'),
        api.get('/api/leases').catch(() => ({ data: [] }))
      ]);
      setProperties(Array.isArray(propsRes.data) ? propsRes.data : []);
      setTenants(Array.isArray(tenantsRes.data) ? tenantsRes.data : []);
      setLeases(Array.isArray(leasesRes.data) ? leasesRes.data : []);
    } catch (error) {
      console.error('❌ Error fetching property/tenant options:', error);
    }
  };

  const getLeaseLabel = (leaseId) => {
    if (!leaseId) return null;
    const lease = leases.find((l) => l.id === leaseId);
    if (!lease) return 'Lease';
    const parts = [getTenantLabel(lease.tenantId), getPropertyLabel(lease.propertyId)].filter(Boolean);
    return parts.length ? parts.join(' · ') : 'Lease';
  };

  const getPropertyLabel = (propertyId) => {
    if (!propertyId) return null;
    const property = properties.find((p) => p.id === propertyId);
    if (!property) return propertyId;
    return property.name || property.address || propertyId;
  };

  const getTenantLabel = (tenantId) => {
    if (!tenantId) return null;
    const tenant = tenants.find((t) => t.id === tenantId);
    if (!tenant) return tenantId;
    const name = [tenant.firstName, tenant.lastName].filter(Boolean).join(' ').trim();
    return name || tenant.email || tenantId;
  };

  const fetchGoogleFolders = async () => {
    try {
      console.log('📁 Fetching Google Drive folders...');
      const response = await api.get('/api/google-workspace-documents/folders');
      setGoogleFolders(response.data);
      console.log('📊 Fetched Google folders:', response.data.length);
    } catch (error) {
      console.error('❌ Error fetching Google folders:', error);
      // Don't show error to user as this is optional functionality
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      console.log('📄 Fetching documents from API...');
      
      // Fetch documents from API
      const documentsResponse = await api.get('/api/documents');
      console.log('📊 Fetched documents:', documentsResponse.data.length);
      const docsList = Array.isArray(documentsResponse.data) ? documentsResponse.data : [];
      setDocuments(docsList);

      // Fetch templates from API
      const templatesResponse = await api.get('/api/templates');
      console.log('📊 Fetched templates:', templatesResponse.data.length);
      setTemplates(templatesResponse.data);

      // Refresh signature requests with document titles resolved
      await fetchSignatureRequests(docsList);

    } catch (error) {
      console.error('❌ Error fetching documents:', error);
      setSnackbar({ open: true, message: 'Failed to fetch documents', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Function to get documents created from a specific template
  const getDocumentsFromTemplate = (templateName) => {
    return documents.filter(doc => 
      doc.title && doc.title.toLowerCase().includes(templateName.toLowerCase().replace(' - document', ''))
    );
  };

  // Enhanced Signature Management Functions
  const getSignatureStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'primary';
      case 'sent': return 'warning';
      case 'completed': return 'success';
      case 'expired': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const handleSendReminder = async (request) => {
    const pending = pendingSignerEmails(request);
    const signerEmail = pending[0]?.email;
    if (!signerEmail) {
      setSnackbar({
        open: true,
        message: 'No pending signer email to remind',
        severity: 'warning'
      });
      return;
    }
    try {
      await api.post(`/api/e-signature/remind/${request.id}`, { signerEmail });
      setSnackbar({
        open: true,
        message: `Reminder sent to ${signerEmail}`,
        severity: 'success'
      });
    } catch (error) {
      console.error('❌ Error sending signature reminder:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to send reminder',
        severity: 'error'
      });
    }
  };

  const handleDownloadSignedDocument = async (request) => {
    try {
      const requestId = request?.id;
      if (!requestId) return;

      // Prefer PDF signed endpoint when available; else open linked document file
      try {
        const response = await api.get(`/api/e-signature/pdf/${requestId}/signed`);
        const url = response.data?.signedPdfUrl || response.data?.pdfUrl || response.data?.url;
        if (url) {
          window.open(url, '_blank');
          setSnackbar({ open: true, message: 'Opening signed document…', severity: 'success' });
          return;
        }
      } catch (pdfErr) {
        console.log('🔍 Signed PDF endpoint unavailable, trying document fileUrl', pdfErr?.response?.status);
      }

      const linked = documents.find((d) => d.id === request.documentId);
      if (linked?.fileUrl) {
        window.open(linked.fileUrl, '_blank');
        setSnackbar({ open: true, message: 'Opening document file…', severity: 'success' });
        return;
      }

      setSnackbar({
        open: true,
        message: 'Signed file not available yet for this request',
        severity: 'warning'
      });
    } catch (error) {
      console.error('❌ Error downloading signed document:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to download signed document',
        severity: 'error'
      });
    }
  };

  const copySigningLink = async (url) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setSnackbar({ open: true, message: 'Signing link copied', severity: 'success' });
    } catch (error) {
      console.error('❌ Clipboard copy failed:', error);
      setSnackbar({ open: true, message: url, severity: 'info' });
    }
  };

  const handleCancelSignatureRequest = async (request, reason = 'Cleared from Documents hub') => {
    if (!request?.id) return;
    try {
      setLoading(true);
      await api.post(`/api/e-signature/cancel/${request.id}`, { reason });
      await fetchSignatureRequests();
      setCancelConfirm({ open: false, request: null });
      setSnackbar({
        open: true,
        message: 'Signature request cancelled',
        severity: 'success'
      });
    } catch (error) {
      console.error('❌ Cancel signature failed:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to cancel signature request',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAllPending = async () => {
    const pending = signatureRequests.filter(
      (r) => r.status === 'pending' || r.status === 'sent'
    );
    if (!pending.length) {
      setCancelAllConfirmOpen(false);
      return;
    }
    try {
      setLoading(true);
      let ok = 0;
      let failed = 0;
      for (const req of pending) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await api.post(`/api/e-signature/cancel/${req.id}`, {
            reason: 'Bulk clear pending test invites'
          });
          ok += 1;
        } catch (err) {
          console.error('❌ Bulk cancel failed for', req.id, err);
          failed += 1;
        }
      }
      await fetchSignatureRequests();
      setCancelAllConfirmOpen(false);
      setSnackbar({
        open: true,
        message:
          failed > 0
            ? `Cancelled ${ok} pending; ${failed} failed`
            : `Cancelled ${ok} pending signature request${ok === 1 ? '' : 's'}`,
        severity: failed > 0 ? 'warning' : 'success'
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteDocument = async () => {
    const doc = deleteConfirm.doc;
    if (!doc?.id) {
      setDeleteConfirm({ open: false, doc: null });
      return;
    }
    try {
      setLoading(true);
      await api.delete(`/api/documents/${doc.id}`);
      await fetchDocuments();
      setSelectedDocIds((prev) => prev.filter((id) => id !== doc.id));
      setDeleteConfirm({ open: false, doc: null });
      setSnackbar({
        open: true,
        message: `Deleted “${doc.title || 'document'}”`,
        severity: 'success'
      });
    } catch (error) {
      console.error('❌ Error deleting document:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to delete document',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmBulkDeleteDocuments = async () => {
    const ids = [...selectedDocIds];
    if (!ids.length) {
      setBulkDeleteConfirmOpen(false);
      return;
    }
    try {
      setLoading(true);
      let ok = 0;
      let failed = 0;
      for (const id of ids) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await api.delete(`/api/documents/${id}`);
          ok += 1;
        } catch (err) {
          console.error('❌ Bulk delete failed for', id, err);
          failed += 1;
        }
      }
      await fetchDocuments();
      setSelectedDocIds([]);
      setBulkDeleteConfirmOpen(false);
      setSnackbar({
        open: true,
        message:
          failed > 0
            ? `Deleted ${ok} document(s); ${failed} failed`
            : `Deleted ${ok} document${ok === 1 ? '' : 's'}`,
        severity: failed > 0 ? 'warning' : 'success'
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter signature requests based on search and status
  const filteredSignatureRequests = signatureRequests.filter((request) => {
    const hay = `${request.documentTitle || ''} ${request.id || ''}`.toLowerCase();
    const matchesSearch = hay.includes((signatureSearchTerm || '').toLowerCase());
    const matchesStatus =
      signatureStatusFilter === 'all' || request.status === signatureStatusFilter;
    // Default "all" still hides cancelled/superseded to reduce clutter
    const hiddenUnlessFiltered =
      signatureStatusFilter === 'all' &&
      (request.status === 'cancelled' || request.status === 'superseded');
    return matchesSearch && matchesStatus && !hiddenUnlessFiltered;
  });

  const pendingSignatureCount = signatureRequests.filter(
    (r) => r.status === 'pending' || r.status === 'sent'
  ).length;

  const resetGoogleDocForm = () => {
    setGoogleDocData({
      title: 'Property Management Document',
      propertyId: '',
      tenantId: '',
      folderId: ''
    });
  };

  const performCreateGoogleDoc = async () => {
    try {
      setLoading(true);
      console.log('📝 Creating Google Doc for Documents hub...');

      const response = await api.post('/api/google-workspace-documents/create-doc', {
        title: googleDocData.title || 'Property Management Document',
        content: 'This is a property management document created from the app.',
        folderId: googleDocData.folderId || undefined,
        propertyId: googleDocData.propertyId || '',
        tenantId: googleDocData.tenantId || ''
      });

      console.log('✅ Google Doc created:', response.data);
      const warnings = response.data?.warnings || response.data?.document?.warnings;
      await fetchDocuments();
      setGoogleDocDialog(false);
      setGoogleDocAttachConfirmOpen(false);
      resetGoogleDocForm();
      setSnackbar({
        open: true,
        message: Array.isArray(warnings) && warnings.length
          ? `Google Doc created with warning: ${warnings[0]}`
          : 'Google Doc created. Check Documents for the registry row.',
        severity: Array.isArray(warnings) && warnings.length ? 'warning' : 'success'
      });
    } catch (error) {
      console.error('❌ Error creating Google Doc:', error);
      setSnackbar({ open: true, message: 'Failed to create Google Doc', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const createGoogleDoc = async () => {
    if (!googleDocData.title?.trim()) {
      setSnackbar({ open: true, message: 'Please enter a document title', severity: 'error' });
      return;
    }
    if (!googleDocData.propertyId && !googleDocData.tenantId) {
      setGoogleDocAttachConfirmOpen(true);
      return;
    }
    await performCreateGoogleDoc();
  };

  const openEditForAttach = (doc) => {
    setEditData({
      id: doc.id,
      title: doc.title,
      description: doc.description || '',
      category: doc.category,
      tags: doc.tags || [],
      status: doc.status,
      expiryDate: doc.expiryDate || '',
      propertyId: doc.propertyId || '',
      tenantId: doc.tenantId || ''
    });
    setEditDialog(true);
  };

  const syncDocumentWithGoogleDrive = async () => {
    try {
      setLoading(true);
      console.log('🔄 Syncing document with Google Drive:', selectedDocumentForSync.id);

      const response = await api.post('/api/google-workspace-documents/sync-document', {
        documentId: selectedDocumentForSync.id,
        folderId: selectedFolder,
        shareWithTenant: false // Could be made configurable
      });

      console.log('✅ Document synced with Google Drive:', response.data);
      
      // Refresh documents list
      await fetchDocuments();
      
      setSyncDialog(false);
      setSelectedDocumentForSync(null);
      setSelectedFolder('');
      setSnackbar({ open: true, message: 'Document synced with Google Drive successfully!', severity: 'success' });
    } catch (error) {
      console.error('❌ Error syncing document:', error);
      setSnackbar({ open: true, message: 'Failed to sync document with Google Drive', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const createSignatureRequest = async () => {
    try {
      if (!signatureRequestData.documentId) {
        setSnackbar({ open: true, message: 'Select a document to request signatures', severity: 'error' });
        return;
      }
      const invalidSigner = signatureRequestData.signers.find(
        (s) => !s.name?.trim() || !s.email?.trim()
      );
      if (!signatureRequestData.signers.length || invalidSigner) {
        setSnackbar({
          open: true,
          message: 'Each signer needs a name and email',
          severity: 'error'
        });
        return;
      }

      setLoading(true);
      console.log('📝 Creating signature request for document:', signatureRequestData.documentId);

      const payload = {
        documentId: signatureRequestData.documentId,
        signers: signatureRequestData.signers,
        message: signatureRequestData.message
      };
      // BYO PDF overlays — BE createSignatureRequest treats fields[] as byoPdf
      if (Array.isArray(signatureRequestData.fields) && signatureRequestData.fields.length > 0) {
        payload.fields = signatureRequestData.fields.map(toApiEsignField);
        payload.byoPdf = true;
      }

      const response = await api.post('/api/e-signature/create-request', payload);

      console.log('✅ Signature request created:', response.data);
      const created = normalizeSignatureRequest(response.data, documents);
      const emailResults = Array.isArray(response.data?.emailResults)
        ? response.data.emailResults
        : [];
      const emailsSent = emailResults.filter((r) => r.success).length;
      const emailsFailed = emailResults.filter((r) => !r.success && !r.skipped).length;

      await fetchDocuments();

      setSignatureRequestDialog(false);
      setSignatureRequestData({
        documentId: '',
        signers: [],
        message: '',
        fields: []
      });
      setCreatedSigningLink(created.signingUrl || response.data?.signingUrl || '');
      setActiveTab(2);

      let snackMessage = 'Signature request created.';
      if (emailResults.length > 0) {
        snackMessage =
          emailsFailed === 0
            ? `Signature request created. Invite emails sent to ${emailsSent} signer${emailsSent === 1 ? '' : 's'}.`
            : emailsSent === 0
              ? `Signature request created, but invite emails failed (${emailsFailed}). Check spam or ask support.`
              : `Signature request created. ${emailsSent} email(s) sent, ${emailsFailed} failed.`;
      } else if (created.signingUrl || response.data?.signingUrl) {
        snackMessage = `Signature request created. Share link: ${created.signingUrl || response.data.signingUrl}`;
      }

      setSnackbar({
        open: true,
        message: snackMessage,
        severity: emailsFailed > 0 && emailsSent === 0 ? 'warning' : emailsFailed > 0 ? 'warning' : 'success'
      });
    } catch (error) {
      console.error('❌ Error creating signature request:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to create signature request',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const SIGNER_ROLE_OPTIONS = [
    'Tenant',
    'Tenant 2',
    'Landlord',
    'Witness',
    'Co-tenant',
    'Signer',
    'Contractor'
  ];

  const addSigner = () => {
    setSignatureRequestData((prev) => {
      const current = Array.isArray(prev.signers) ? prev.signers : [];
      const defaults = ['Tenant', 'Tenant 2', 'Landlord', 'Witness'];
      const used = new Set(current.map((s) => s.role).filter(Boolean));
      const role = defaults.find((r) => !used.has(r)) || 'Signer';
      return {
        ...prev,
        signers: [...current, { name: '', email: '', role }]
      };
    });
  };

  const removeSigner = (index) => {
    setSignatureRequestData((prev) => ({
      ...prev,
      signers: (Array.isArray(prev.signers) ? prev.signers : []).filter((_, i) => i !== index)
    }));
  };

  const updateSigner = (index, field, value) => {
    setSignatureRequestData((prev) => ({
      ...prev,
      signers: (Array.isArray(prev.signers) ? prev.signers : []).map((signer, i) =>
        i === index ? { ...signer, [field]: value } : signer
      )
    }));
  };

  const openSignatureRequestDialog = (doc = null) => {
    setCreatedSigningLink('');
    setSignatureRequestData((prev) => {
      const signers = Array.isArray(prev.signers) && prev.signers.length
        ? prev.signers
        : [{ name: '', email: '', role: 'Tenant' }];
      return {
        documentId: doc?.id || prev.documentId || '',
        signers,
        message: prev.message || '',
        fields: Array.isArray(prev.fields) ? prev.fields : []
      };
    });
    setSignatureRequestDialog(true);
  };

  const openPlaceFields = (doc) => {
    if (!doc?.id) {
      setSnackbar({ open: true, message: 'Document not ready for field placement', severity: 'warning' });
      return;
    }
    // Prefer fresh list row so saved esignFields count/labels stay in sync
    const fresh = documents.find((d) => d.id === doc.id) || doc;
    setFieldPlacerDoc(fresh);
    setFieldPlacerOpen(true);
    setSignatureRequestData((prev) => ({
      ...prev,
      documentId: fresh.id,
      signers: prev.signers.length ? prev.signers : [{ name: '', email: '', role: 'Tenant' }]
    }));
  };

  const performUpload = async () => {
    try {
      setLoading(true);
      console.log('📄 Uploading document:', uploadData.title);

      const formData = new FormData();
      formData.append('file', uploadData.file, uploadData.file.name || 'document.pdf');
      formData.append('title', uploadData.title);
      formData.append('description', uploadData.description || '');
      formData.append('category', uploadData.category);
      formData.append('propertyId', uploadData.propertyId || '');
      formData.append('tenantId', uploadData.tenantId || '');
      formData.append(
        'tags',
        Array.isArray(uploadData.tags) ? uploadData.tags.join(', ') : uploadData.tags || ''
      );
      formData.append('expiryDate', uploadData.expiryDate || '');

      // Do not set Content-Type manually — browser must add multipart boundary.
      // Uploads on mobile need a longer timeout than the default 10s.
      const response = await api.post('/api/documents', formData, {
        timeout: 120000
      });

      console.log('✅ Document uploaded:', response.data);
      const warnings = response.data?.warnings || response.data?.document?.warnings;
      const uploadedDoc = {
        id: response.data?.id,
        ...response.data,
        fileUrl: response.data?.fileUrl || null,
        fileName: response.data?.fileName || null,
        title: response.data?.title || uploadData.title,
        type: response.data?.type || 'pdf'
      };
      await fetchDocuments();
      setUploadDialog(false);
      setAttachConfirmOpen(false);

      const looksPdf =
        String(uploadedDoc.type || '').toLowerCase().includes('pdf') ||
        /\.pdf$/i.test(String(uploadedDoc.fileName || uploadData.file?.name || '')) ||
        uploadData.file?.type === 'application/pdf';

      setUploadData({
        title: '',
        description: '',
        category: '',
        propertyId: '',
        tenantId: '',
        tags: [],
        file: null,
        expiryDate: ''
      });

      if (looksPdf && uploadedDoc.id) {
        setSnackbar({
          open: true,
          message: Array.isArray(warnings) && warnings.length
            ? `Uploaded with warning: ${warnings[0]}. Place fields next.`
            : 'Uploaded — place merge/signature fields on the PDF',
          severity: Array.isArray(warnings) && warnings.length ? 'warning' : 'success'
        });
        openPlaceFields(uploadedDoc);
      } else {
        setSnackbar({
          open: true,
          message: Array.isArray(warnings) && warnings.length
            ? `Uploaded with warning: ${warnings[0]}`
            : 'Document uploaded successfully!',
          severity: Array.isArray(warnings) && warnings.length ? 'warning' : 'success'
        });
      }
    } catch (error) {
      console.error('❌ Error uploading document:', error);
      const apiMsg =
        error.response?.data?.details ||
        error.response?.data?.error ||
        error.message ||
        'Failed to upload document';
      setSnackbar({
        open: true,
        message: String(apiMsg),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const uploadDocument = async () => {
    if (!uploadData.title || !uploadData.category || !uploadData.file) {
      setSnackbar({ open: true, message: 'Please fill in all required fields and select a file', severity: 'error' });
      return;
    }

    // F4: warn/block when unattached — confirm before proceeding without property or tenant
    if (!uploadData.propertyId && !uploadData.tenantId) {
      setAttachConfirmOpen(true);
      return;
    }

    await performUpload();
  };

  const createTemplate = async () => {
    try {
      setLoading(true);
      
      console.log('📄 Creating template:', templateData.name);

      // Create template via API
      const response = await api.post('/api/templates', templateData);

      console.log('✅ Template created:', response.data);
      
      // Refresh templates list
      await fetchDocuments();
      
      setTemplateDialog(false);
      setSnackbar({ open: true, message: 'Template created successfully!', severity: 'success' });
      
      // Reset form
      setTemplateData({
        name: '',
        category: '',
        description: '',
        content: ''
      });
    } catch (error) {
      console.error('❌ Error creating template:', error);
      setSnackbar({ open: true, message: 'Failed to create template', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const updateTemplate = async () => {
    try {
      setLoading(true);
      
      console.log('📄 Updating template:', editingTemplate.id);

      // Update template via API
      const response = await api.put(`/api/templates/${editingTemplate.id}`, templateData);

      console.log('✅ Template updated:', response.data);
      
      // Refresh templates list
      await fetchDocuments();
      
      setTemplateDialog(false);
      setEditingTemplate(null);
      setSnackbar({ open: true, message: 'Template updated successfully!', severity: 'success' });
      
      // Reset form
      setTemplateData({
        name: '',
        category: '',
        description: '',
        content: ''
      });
    } catch (error) {
      console.error('❌ Error updating template:', error);
      setSnackbar({ open: true, message: 'Failed to update template', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleUseTemplate = (template) => {
    console.log('📄 Using template:', template.name);
    
    // Pre-fill the upload form with template data, ensuring no undefined values
    setUploadData({
      ...uploadData,
      title: `${template.name || 'Template'} - Document`,
      category: template.type || template.category || 'Form', // Fallback to 'Form' if undefined
      description: template.description || '',
      tags: template.tags ? (Array.isArray(template.tags) ? template.tags.join(', ') : template.tags) : ''
    });
    
    // Close template dialog and open upload dialog
    setTemplateDialog(false);
    setUploadDialog(true);
    
    setSnackbar({ open: true, message: `Template "${template.name}" loaded! Upload a file to create a new document.`, severity: 'success' });
  };

  const handleEditTemplate = (template) => {
    console.log('📄 Editing template:', template.name);
    
    // Set editing template
    setEditingTemplate(template);
    
    // Pre-fill the template form with existing data, ensuring no undefined values
    setTemplateData({
      name: template.name || '',
      category: template.type || template.category || 'Form', // Fallback to 'Form' if undefined
      content: template.content || '',
      description: template.description || '',
      tags: template.tags || []
    });
    
    // Open template dialog in edit mode
    setTemplateDialog(true);
    
    setSnackbar({ open: true, message: `Editing template "${template.name}"`, severity: 'info' });
  };

  const handleDeleteTemplate = async (template) => {
    console.log('📄 Deleting template:', template.name);
    
    // Confirm deletion
    if (window.confirm(`Are you sure you want to delete the template "${template.name}"? This action cannot be undone.`)) {
      try {
        setLoading(true);
        
        // Delete template via API
        await api.delete(`/api/templates/${template.id}`);
        
        console.log('✅ Template deleted:', template.id);
        
        // Refresh templates list
        await fetchDocuments();
        
        setSnackbar({ open: true, message: `Template "${template.name}" deleted successfully`, severity: 'success' });
      } catch (error) {
        console.error('❌ Error deleting template:', error);
        setSnackbar({ open: true, message: 'Failed to delete template', severity: 'error' });
      } finally {
        setLoading(false);
      }
    }
  };

  const signDocument = async () => {
    try {
      setLoading(true);
      
      console.log('📄 Signing document:', signatureData.documentId);

      // Sign document via API
      const response = await api.post(`/api/documents/${signatureData.documentId}/sign`, {
        signerName: signatureData.signerName,
        signerEmail: signatureData.signerEmail,
        signature: signatureData.signature,
        role: 'Signer'
      });

      console.log('✅ Document signed:', response.data);
      
      // Refresh documents list
      await fetchDocuments();
      
      setSignDialog(false);
      setSnackbar({ open: true, message: 'Document signed successfully!', severity: 'success' });
      
      // Reset form
      setSignatureData({
        documentId: '',
        signerName: '',
        signerEmail: '',
        signature: ''
      });
    } catch (error) {
      console.error('❌ Error signing document:', error);
      setSnackbar({ open: true, message: 'Failed to sign document', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const deleteDocument = (documentId) => {
    const doc = documents.find((d) => d.id === documentId) || { id: documentId };
    setDeleteConfirm({ open: true, doc });
  };

  const editDocument = async () => {
    try {
      setLoading(true);
      
      // Validation
      if (!editData.title || !editData.category) {
        setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'error' });
        return;
      }

      console.log('📄 Updating document:', editData.id);

      // Update document via API
      const response = await api.put(`/api/documents/${editData.id}`, {
        title: editData.title,
        description: editData.description,
        category: editData.category,
        tags: editData.tags.join(', '),
        status: editData.status,
        expiryDate: editData.expiryDate || '',
        propertyId: editData.propertyId || '',
        tenantId: editData.tenantId || ''
      });

      console.log('✅ Document updated:', response.data);
      const warnings = response.data?.warnings;

      // Refresh documents list
      await fetchDocuments();
      
      setEditDialog(false);
      setSnackbar({
        open: true,
        message: Array.isArray(warnings) && warnings.length
          ? `Updated with warning: ${warnings[0]}`
          : 'Document updated successfully!',
        severity: Array.isArray(warnings) && warnings.length ? 'warning' : 'success'
      });
      
      // Reset form
      setEditData({
        id: '',
        title: '',
        description: '',
        category: '',
        tags: [],
        status: '',
        expiryDate: '',
        propertyId: '',
        tenantId: ''
      });
    } catch (error) {
      console.error('❌ Error updating document:', error);
      setSnackbar({ open: true, message: 'Failed to update document', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'signed': return 'success';
      case 'pending': return 'warning';
      case 'approved': return 'info';
      case 'active': return 'primary';
      default: return 'default';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Lease': return <HomeIcon />;
      case 'Contract': return <WorkIcon />;
      case 'Permit': return <SecurityIcon />;
      case 'Insurance': return <CheckCircleIcon />;
      default: return <DocumentIcon />;
    }
  };

  const filteredDocuments = documents.filter((doc) => {
    if (filters.category !== 'all' && doc.category !== filters.category) return false;
    if (filters.status !== 'all' && doc.status !== filters.status) return false;

    if (filters.attachment === 'attached' && !isDocAttached(doc)) return false;
    if (filters.attachment === 'unattached' && isDocAttached(doc)) return false;

    if (filters.drive === 'synced' && !isDocDriveSynced(doc)) return false;
    if (filters.drive === 'not_synced' && isDocDriveSynced(doc)) return false;

    if (filters.entityType === 'property') {
      if (!doc.propertyId) return false;
      if (filters.entityId && doc.propertyId !== filters.entityId) return false;
    }
    if (filters.entityType === 'lease') {
      if (!doc.leaseId) return false;
      if (filters.entityId && doc.leaseId !== filters.entityId) return false;
    }
    if (filters.entityType === 'tenant') {
      if (!doc.tenantId) return false;
      if (filters.entityId && doc.tenantId !== filters.entityId) return false;
    }

    if (filters.search) {
      const q = filters.search.toLowerCase();
      const hay = [
        doc.title,
        doc.description,
        doc.category,
        getPropertyLabel(doc.propertyId),
        getTenantLabel(doc.tenantId),
        getLeaseLabel(doc.leaseId)
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }

    return true;
  });

  const filteredDocIds = filteredDocuments.map((d) => d.id).filter(Boolean);
  const allFilteredSelected =
    filteredDocIds.length > 0 && filteredDocIds.every((id) => selectedDocIds.includes(id));
  const someFilteredSelected = filteredDocIds.some((id) => selectedDocIds.includes(id));

  const esignFieldCount = (doc) => {
    if (Array.isArray(doc?.esignFields)) return doc.esignFields.length;
    return 0;
  };

  const toggleSelectDoc = (id) => {
    setSelectedDocIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      setSelectedDocIds((prev) => prev.filter((id) => !filteredDocIds.includes(id)));
    } else {
      setSelectedDocIds((prev) => [...new Set([...prev, ...filteredDocIds])]);
    }
  };
  const handleFileUpload = (event) => {
    pickingUploadFileRef.current = false;
    const file = event.target.files?.[0];
    if (!file) {
      console.log('🔍 File picker returned no file');
      return;
    }

    console.log('📄 File selected:', file.name, file.type, file.size);
    const baseName = String(file.name || '')
      .replace(/\.[^.]+$/, '')
      .replace(/[_-]+/g, ' ')
      .trim();
    setUploadData((prev) => ({
      ...prev,
      file,
      title: prev.title || baseName,
      category:
        prev.category ||
        (/\blease\b/i.test(file.name) || file.type === 'application/pdf' ? 'Lease' : prev.category)
    }));
    setSnackbar({
      open: true,
      message: `Selected ${file.name}`,
      severity: 'success'
    });
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: { xs: 2, sm: 3 },
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography 
          variant="h4" 
          gutterBottom
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, mb: { xs: 1, sm: 0 } }}
        >
          {isMobile ? 'Documents' : 'Documents'}
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 1, sm: 2 },
          width: { xs: '100%', sm: 'auto' },
          flexDirection: { xs: 'column', sm: 'row' }
        }}>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => setUploadDialog(true)}
            fullWidth={isMobile}
            size={isMobile ? 'small' : 'medium'}
          >
            {isMobile ? 'Upload' : 'Upload Document'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setTemplateDialog(true)}
            fullWidth={isMobile}
            size={isMobile ? 'small' : 'medium'}
          >
            {isMobile ? 'Template' : 'Create Template'}
          </Button>
          {googleEnabled && (
            <Button
              variant="outlined"
              startIcon={<AttachIcon />}
              onClick={() => {
                resetGoogleDocForm();
                setGoogleDocDialog(true);
              }}
              fullWidth={isMobile}
              size={isMobile ? 'small' : 'medium'}
            >
              {isMobile ? 'Google Doc' : 'Create Google Doc'}
            </Button>
          )}
        </Box>
      </Box>

      <Tabs 
        value={activeTab} 
        onChange={(e, newValue) => setActiveTab(newValue)} 
        sx={{ mb: { xs: 2, sm: 3 } }}
        variant={isMobile ? 'fullWidth' : 'standard'}
      >
        <Tab label="Documents" />
        <Tab label="Templates" />
        <Tab label="Signatures" />
      </Tabs>

      {activeTab === 0 && (
        <Box>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            mb: { xs: 1.5, sm: 2 },
            gap: { xs: 1.5, sm: 0 }
          }}>
            <Typography 
              variant="h6"
              sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
            >
              All Documents
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              gap: { xs: 1, sm: 1 },
              width: { xs: '100%', sm: 'auto' },
              flexDirection: { xs: 'column', sm: 'row' },
              flexWrap: 'wrap'
            }}>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 130 } }}>
                <InputLabel>Entity type</InputLabel>
                <Select
                  value={filters.entityType}
                  onChange={(e) => setFilters({ ...filters, entityType: e.target.value, entityId: '' })}
                  label="Entity type"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="property">Property</MenuItem>
                  <MenuItem value="tenant">Tenant</MenuItem>
                  <MenuItem value="lease">Lease</MenuItem>
                </Select>
              </FormControl>
              {filters.entityType !== 'all' && (
                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 } }}>
                  <InputLabel>Entity</InputLabel>
                  <Select
                    value={filters.entityId}
                    onChange={(e) => setFilters({ ...filters, entityId: e.target.value })}
                    label="Entity"
                  >
                    <MenuItem value="">All</MenuItem>
                    {filters.entityType === 'property' &&
                      properties.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.name || p.address || p.id}
                        </MenuItem>
                      ))}
                    {filters.entityType === 'tenant' &&
                      tenants.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          {[t.firstName, t.lastName].filter(Boolean).join(' ') || t.email || t.id}
                        </MenuItem>
                      ))}
                    {filters.entityType === 'lease' &&
                      leases.map((l) => (
                        <MenuItem key={l.id} value={l.id}>
                          {getLeaseLabel(l.id)}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 130 } }}>
                <InputLabel>Attachment</InputLabel>
                <Select
                  value={filters.attachment}
                  onChange={(e) => setFilters({ ...filters, attachment: e.target.value })}
                  label="Attachment"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="attached">Attached</MenuItem>
                  <MenuItem value="unattached">Unattached</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 130 } }}>
                <InputLabel>Drive</InputLabel>
                <Select
                  value={filters.drive}
                  onChange={(e) => setFilters({ ...filters, drive: e.target.value })}
                  label="Drive"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="synced">Synced</MenuItem>
                  <MenuItem value="not_synced">Not synced</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 }, width: { xs: '100%', sm: 'auto' } }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={filters.category}
                  onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                  label="Category"
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  <MenuItem value="Lease">Lease</MenuItem>
                  <MenuItem value="Contract">Contract</MenuItem>
                  <MenuItem value="Permit">Permit</MenuItem>
                  <MenuItem value="Insurance">Insurance</MenuItem>
                  <MenuItem value="Form">Form</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 }, width: { xs: '100%', sm: 'auto' } }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="signed">Signed</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Search"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                sx={{ minWidth: { xs: '100%', sm: 140 } }}
              />
            </Box>
          </Box>

          {(selectedDocIds.length > 0 || filteredDocuments.length > 0) && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                alignItems: 'center',
                mb: 1.5
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {selectedDocIds.length
                  ? `${selectedDocIds.length} selected`
                  : `${filteredDocuments.length} document${filteredDocuments.length === 1 ? '' : 's'}`}
              </Typography>
              {selectedDocIds.length > 0 && (
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  startIcon={<DeleteIcon />}
                  onClick={() => setBulkDeleteConfirmOpen(true)}
                  disabled={loading}
                >
                  Delete selected
                </Button>
              )}
              {selectedDocIds.length > 0 && (
                <Button size="small" onClick={() => setSelectedDocIds([])}>
                  Clear selection
                </Button>
              )}
            </Box>
          )}
          
          <TableContainer 
            component={Paper}
            sx={{ 
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            <Table sx={{ minWidth: { xs: 800, sm: 'auto' } }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      indeterminate={someFilteredSelected && !allFilteredSelected}
                      checked={allFilteredSelected}
                      onChange={toggleSelectAllFiltered}
                      inputProps={{ 'aria-label': 'Select all visible documents' }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 'bold' }}>
                    Document
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }}>
                    Category
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 'bold', display: { xs: 'none', sm: 'table-cell' } }}>
                    Property
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }}>
                    Tenant
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 'bold', display: { xs: 'none', sm: 'table-cell' } }}>
                    Drive
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 'bold' }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 'bold' }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDocuments.map((doc) => (
                  <TableRow key={doc.id} selected={selectedDocIds.includes(doc.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedDocIds.includes(doc.id)}
                        onChange={() => toggleSelectDoc(doc.id)}
                        inputProps={{ 'aria-label': `Select ${doc.title || doc.id}` }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: { xs: 0.5, sm: 1 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: { xs: 1, sm: 2 }, bgcolor: 'primary.main', width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                          {getCategoryIcon(doc.category)}
                        </Avatar>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap' }}>
                            <Typography 
                              variant="body2" 
                              fontWeight="bold"
                              sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                            >
                              {doc.title}
                            </Typography>
                            {doc.type === 'pdf' && (
                              <Chip 
                                label={doc.fileUrl || doc.storagePath || doc.id ? 'Preview' : 'No Preview'} 
                                size="small" 
                                color={doc.fileUrl || doc.storagePath || doc.id ? 'success' : 'warning'}
                                variant="outlined"
                                clickable={Boolean(doc.fileUrl || doc.storagePath || doc.id)}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (doc.fileUrl || doc.storagePath || doc.id) openDocumentFile(doc);
                                }}
                                sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                              />
                            )}
                            {(doc.fileUrl || doc.fileName || doc.storagePath || doc.id) && (
                              <Chip
                                label={
                                  esignFieldCount(doc) > 0
                                    ? `Edit fields · ${esignFieldCount(doc)}`
                                    : 'Place fields'
                                }
                                size="small"
                                color="primary"
                                variant={esignFieldCount(doc) > 0 ? 'filled' : 'outlined'}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openPlaceFields(doc);
                                }}
                                sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                              />
                            )}
                          </Box>
                          <Typography 
                            variant="caption" 
                            color="textSecondary"
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                          >
                            {doc.description}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: { xs: 0.5, sm: 1 }, display: { xs: 'none', md: 'table-cell' } }}>
                      <Chip 
                        label={doc.category} 
                        size="small"
                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: { xs: 0.5, sm: 1 }, display: { xs: 'none', sm: 'table-cell' } }}>
                      {doc.propertyId || doc.leaseId ? (
                        <Box>
                          {doc.propertyId && (
                            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                              {getPropertyLabel(doc.propertyId)}
                            </Typography>
                          )}
                          {doc.leaseId && (
                            <Chip
                              label="Lease"
                              size="small"
                              variant="outlined"
                              color="primary"
                              title={getLeaseLabel(doc.leaseId)}
                              onClick={() =>
                                setFilters((prev) => ({ ...prev, entityType: 'lease', entityId: doc.leaseId }))
                              }
                              sx={{ mt: 0.5, height: 20, fontSize: '0.7rem', cursor: 'pointer' }}
                            />
                          )}
                        </Box>
                      ) : (
                        <Chip
                          label="Unattached"
                          size="small"
                          color="warning"
                          variant="outlined"
                          onClick={() => openEditForAttach(doc)}
                          sx={{ cursor: 'pointer' }}
                          title="Attach property or tenant"
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ py: { xs: 0.5, sm: 1 }, display: { xs: 'none', md: 'table-cell' } }}>
                      {doc.tenantId ? (
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {getTenantLabel(doc.tenantId)}
                        </Typography>
                      ) : (
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => openEditForAttach(doc)}
                          sx={{ minWidth: 0, px: 0.5, fontSize: '0.75rem', textTransform: 'none' }}
                        >
                          Attach…
                        </Button>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: { xs: 0.5, sm: 1 }, display: { xs: 'none', sm: 'table-cell' } }}>
                      {isDocDriveSynced(doc) ? (
                        <Chip
                          icon={<CloudDoneIcon />}
                          label="Synced"
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                        />
                      ) : (
                        <Chip
                          label="Local only"
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ py: { xs: 0.5, sm: 1 } }}>
                      <Chip 
                        label={doc.status} 
                        color={getStatusColor(doc.status)}
                        size="small"
                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: { xs: 0.5, sm: 1 } }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: { xs: 0.5, sm: 1 },
                          alignItems: 'center',
                          maxWidth: { xs: 220, sm: 'none' }
                        }}
                      >
                        {(doc.fileUrl || doc.fileName || doc.id) && (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<DrawIcon />}
                            onClick={() => openPlaceFields(doc)}
                            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                          >
                            {esignFieldCount(doc) > 0 ? 'Edit fields' : 'Place fields'}
                          </Button>
                        )}
                        {driveUrlFor(doc) && (
                          <IconButton
                            size="small"
                            onClick={() => window.open(driveUrlFor(doc), '_blank')}
                            title="Open in Drive"
                          >
                            <OpenInNewIcon color="primary" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedDocument(doc);
                            setViewDialog(true);
                          }}
                          title={doc.fileUrl ? "View PDF" : "View Document Info (PDF preview not available)"}
                        >
                          <ViewIcon color={doc.fileUrl ? "primary" : "disabled"} />
                        </IconButton>
                        <IconButton 
                          size="small"
                          onClick={() => {
                            if (doc.fileUrl || doc.id) {
                              openDocumentFile(doc, { download: true });
                            } else {
                              setSnackbar({ open: true, message: 'File not available for download', severity: 'warning' });
                            }
                          }}
                          title="Download Document"
                        >
                          <DownloadIcon />
                        </IconButton>
                        <IconButton 
                          size="small"
                          onClick={() => openEditForAttach(doc)}
                          title="Edit Document"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          size="small"
                          onClick={() => deleteDocument(doc.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                        {googleEnabled && (
                          <IconButton 
                            size="small"
                            onClick={() => {
                              setSelectedDocumentForSync(doc);
                              setSyncDialog(true);
                            }}
                            title="Sync with Google Drive"
                          >
                            <AttachIcon />
                          </IconButton>
                        )}
                        <IconButton 
                          size="small"
                          onClick={() => openSignatureRequestDialog(doc)}
                          title="Request Signatures"
                        >
                          <SecurityIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredDocuments.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <DocumentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {documents.length === 0
                  ? 'No documents yet'
                  : 'No documents match these filters'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 420, mx: 'auto' }}>
                {filters.attachment === 'unattached'
                  ? 'Unattached files stay hard to find. Open Edit on a row and attach a property or tenant.'
                  : documents.length === 0
                    ? 'Upload your first lease or insurance file — attach a property so you can find it later.'
                    : 'Try clearing Entity / Attachment / Drive filters, or upload a new file.'}
              </Typography>
              {documents.length === 0 && (
                <Button variant="contained" startIcon={<UploadIcon />} onClick={() => setUploadDialog(true)}>
                  Upload Document
                </Button>
              )}
            </Box>
          )}
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          <Typography variant="h6" gutterBottom>Document Templates</Typography>
          <Grid container spacing={3}>
            {templates.map((template) => (
              <Grid item xs={12} md={6} lg={4} key={template.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ mr: 2, bgcolor: 'secondary.main', position: 'relative' }}>
                        <DocumentIcon />
                        {(() => {
                          const templateDocuments = getDocumentsFromTemplate(template.name);
                          return templateDocuments.length > 0 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: -5,
                                right: -5,
                                bgcolor: 'success.main',
                                borderRadius: '50%',
                                width: 16,
                                height: 16,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.7rem',
                                color: 'white',
                                fontWeight: 'bold'
                              }}
                            >
                              {templateDocuments.length}
                            </Box>
                          );
                        })()}
                      </Avatar>
                      <Box>
                        <Typography variant="h6">
                          {template.name}
                        </Typography>
                        <Typography color="textSecondary">
                          {template.category}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      {template.description}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body2" color="textSecondary">
                        Used {template.usageCount} times
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {template.createdDate}
                      </Typography>
                    </Box>

                    {/* Documents created from this template */}
                    {(() => {
                      const templateDocuments = getDocumentsFromTemplate(template.name);
                      return templateDocuments.length > 0 && (
                        <Box sx={{ mb: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <LibraryBooksIcon sx={{ fontSize: 16, mr: 1, color: 'primary.main' }} />
                            <Typography variant="body2" color="primary.main" sx={{ fontWeight: 'medium' }}>
                              Documents Created ({templateDocuments.length})
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {templateDocuments.slice(0, 3).map((doc, index) => (
                              <Chip
                                key={index}
                                size="small"
                                icon={doc.type === 'pdf' || doc.fileUrl?.includes('.pdf') ? <PdfIcon /> : <FileIcon />}
                                label={doc.title.length > 20 ? doc.title.substring(0, 20) + '...' : doc.title}
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            ))}
                            {templateDocuments.length > 3 && (
                              <Chip
                                size="small"
                                label={`+${templateDocuments.length - 3} more`}
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            )}
                          </Box>
                        </Box>
                      );
                    })()}
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button 
                        size="small" 
                        variant="outlined"
                        onClick={() => handleUseTemplate(template)}
                      >
                        Use Template
                      </Button>
                      {template.shared ? (
                        <Chip size="small" label="ZenRentFlow starter" variant="outlined" sx={{ alignSelf: 'center' }} />
                      ) : (
                        <>
                          <Button 
                            size="small" 
                            variant="outlined"
                            onClick={() => handleEditTemplate(template)}
                          >
                            Edit
                          </Button>
                          <Button 
                            size="small" 
                            variant="outlined"
                            color="error"
                            onClick={() => handleDeleteTemplate(template)}
                          >
                            Delete
                          </Button>
                        </>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {activeTab === 2 && (
        <Box>
          {/* Signature Dashboard Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h5" gutterBottom>Digital Signatures</Typography>
              <Typography variant="body2" color="textSecondary">
                Live signature requests from Documents — share the signing link with each signer
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<SecurityIcon />}
              onClick={() => openSignatureRequestDialog()}
              sx={{ minWidth: 200 }}
            >
              Request Signatures
            </Button>
          </Box>

          {createdSigningLink && (
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              onClose={() => setCreatedSigningLink('')}
              action={
                <Button color="inherit" size="small" onClick={() => copySigningLink(createdSigningLink)}>
                  Copy link
                </Button>
              }
            >
              Share this signing link with the signer: {createdSigningLink}
            </Alert>
          )}

          {/* Signature Statistics */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="primary" gutterBottom>
                  {signatureRequests.filter(req => req.status === 'pending').length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Pending Requests
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="success.main" gutterBottom>
                  {signatureRequests.filter(req => req.status === 'completed').length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Completed
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="warning.main" gutterBottom>
                  {signatureRequests.filter(req => req.status === 'sent').length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Sent for Review
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="error.main" gutterBottom>
                  {signatureRequests.filter(req => req.status === 'expired' || req.status === 'cancelled').length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Expired / Cancelled
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Filter and Search */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search signature requests..."
              value={signatureSearchTerm}
              onChange={(e) => setSignatureSearchTerm(e.target.value)}
              sx={{ minWidth: 300 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={signatureStatusFilter}
                onChange={(e) => setSignatureStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="all">All (hide cancelled)</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="superseded">Superseded</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={signaturesLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={() => fetchSignatureRequests()}
              disabled={signaturesLoading}
            >
              Refresh
            </Button>
            {pendingSignatureCount > 0 && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={<CancelIcon />}
                onClick={() => setCancelAllConfirmOpen(true)}
                disabled={loading || signaturesLoading}
              >
                Cancel all pending ({pendingSignatureCount})
              </Button>
            )}
          </Box>

          {signaturesLoading && signatureRequests.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading signature requests…
              </Typography>
            </Box>
          ) : (
          <>
          {/* Signature Requests List */}
          <Grid container spacing={3}>
            {filteredSignatureRequests.map((request) => (
              <Grid item xs={12} md={6} lg={4} key={request.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" gutterBottom sx={{ flexGrow: 1 }}>
                        {request.documentTitle}
                      </Typography>
                      <Chip 
                        label={request.status} 
                        color={getSignatureStatusColor(request.status)} 
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Box>
                    
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      <strong>Request ID:</strong> {request.id}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      <strong>Created:</strong> {request.createdDate}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      <strong>Expires:</strong> {request.expiryDate}
                    </Typography>
                    {request.signingUrl && request.status === 'pending' && (
                      <Typography
                        variant="body2"
                        color="primary"
                        sx={{ mt: 1, wordBreak: 'break-all', cursor: 'pointer' }}
                        onClick={() => copySigningLink(request.signingUrl)}
                        title="Click to copy"
                      >
                        {request.signingUrl}
                      </Typography>
                    )}
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Typography variant="subtitle2" gutterBottom>
                      Signers ({request.signers.length}):
                    </Typography>
                    
                    <List dense>
                      {request.signers.slice(0, 3).map((signer, index) => (
                        <ListItem key={index} sx={{ px: 0 }}>
                          <ListItemAvatar>
                            <Avatar sx={{ width: 32, height: 32 }}>
                              <PersonIcon fontSize="small" />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={signer.name}
                            secondary={signer.email}
                            primaryTypographyProps={{ variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                          <Box sx={{ ml: 1 }}>
                            {signer.status === 'signed' ? (
                              <CheckCircleIcon color="success" fontSize="small" />
                            ) : signer.status === 'pending' ? (
                              <ScheduleIcon color="warning" fontSize="small" />
                            ) : (
                              <CancelIcon color="error" fontSize="small" />
                            )}
                          </Box>
                        </ListItem>
                      ))}
                      {request.signers.length > 3 && (
                        <ListItem sx={{ px: 0 }}>
                          <ListItemText
                            primary={`+${request.signers.length - 3} more signers`}
                            primaryTypographyProps={{ variant: 'caption', color: 'textSecondary' }}
                          />
                        </ListItem>
                      )}
                    </List>
                  </CardContent>
                  
                  <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Button
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => {
                        setSelectedSignatureRequest(request);
                        setViewSignatureDialog(true);
                      }}
                    >
                      View Details
                    </Button>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {request.status === 'pending' && request.signingUrl && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => copySigningLink(request.signingUrl)}
                        >
                          Copy link
                        </Button>
                      )}
                      {request.status === 'pending' && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<SendIcon />}
                          onClick={() => handleSendReminder(request)}
                        >
                          Remind
                        </Button>
                      )}
                      {(request.status === 'pending' || request.status === 'sent') && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          startIcon={<CancelIcon />}
                          onClick={() => setCancelConfirm({ open: true, request })}
                        >
                          Cancel
                        </Button>
                      )}
                      {request.status === 'completed' && (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownloadSignedDocument(request)}
                        >
                          Download
                        </Button>
                      )}
                    </Box>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Empty State */}
          {filteredSignatureRequests.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <SecurityIcon sx={{ fontSize: 64, color: 'textSecondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No Signature Requests Found
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {signatureSearchTerm || signatureStatusFilter !== 'all' 
                  ? 'Try adjusting your search or filter criteria'
                  : 'Start by requesting signatures for your documents'
                }
              </Typography>
              {!signatureSearchTerm && signatureStatusFilter === 'all' && (
                <Button
                  variant="contained"
                  startIcon={<SecurityIcon />}
                  onClick={() => openSignatureRequestDialog()}
                  sx={{ mt: 2 }}
                >
                  Request Your First Signature
                </Button>
              )}
            </Box>
          )}
          </>
          )}
        </Box>
      )}

      {/* Upload Dialog */}
      <Dialog 
        open={uploadDialog} 
        onClose={(_event, reason) => {
          // iOS file picker can fire a spurious backdrop click when returning
          if (pickingUploadFileRef.current) return;
          if (reason === 'backdropClick' && isMobile) return;
          setUploadDialog(false);
        }} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
        disableRestoreFocus
        keepMounted={false}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Upload Document</DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
            {ATTACH_WARN_MESSAGE}
          </Alert>
          {!uploadData.propertyId && !uploadData.tenantId && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              No property or tenant selected — upload will ask you to confirm before continuing.
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Document Title"
                value={uploadData.title}
                onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={uploadData.description}
                onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="upload-property-label" shrink>
                  Property
                </InputLabel>
                <Select
                  labelId="upload-property-label"
                  value={uploadData.propertyId}
                  onChange={(e) => setUploadData({ ...uploadData, propertyId: e.target.value })}
                  label="Property"
                  displayEmpty
                  notched
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {properties.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name || p.address || p.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="upload-tenant-label" shrink>
                  Tenant
                </InputLabel>
                <Select
                  labelId="upload-tenant-label"
                  value={uploadData.tenantId}
                  onChange={(e) => setUploadData({ ...uploadData, tenantId: e.target.value })}
                  label="Tenant"
                  displayEmpty
                  notched
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {tenants.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name ||
                        t.fullName ||
                        [t.firstName, t.lastName].filter(Boolean).join(' ') ||
                        t.email ||
                        t.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="upload-category-label" shrink>
                  Category
                </InputLabel>
                <Select
                  labelId="upload-category-label"
                  value={uploadData.category}
                  onChange={(e) => setUploadData({ ...uploadData, category: e.target.value })}
                  label="Category"
                  displayEmpty
                  notched
                >
                  <MenuItem value="">
                    <em>Select category</em>
                  </MenuItem>
                  <MenuItem value="Lease">Lease</MenuItem>
                  <MenuItem value="Contract">Contract</MenuItem>
                  <MenuItem value="Permit">Permit</MenuItem>
                  <MenuItem value="Insurance">Insurance</MenuItem>
                  <MenuItem value="Form">Form</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tags"
                value={Array.isArray(uploadData.tags) ? uploadData.tags.join(', ') : uploadData.tags}
                onChange={(e) =>
                  setUploadData({
                    ...uploadData,
                    tags: e.target.value
                      .split(',')
                      .map((t) => t.trim())
                      .filter(Boolean)
                  })
                }
                placeholder="lease, residential, signed"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expiry Date"
                type="date"
                value={uploadData.expiryDate}
                onChange={(e) => setUploadData({ ...uploadData, expiryDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              {/*
                iOS Safari: prefer <label> + real file input (not Button component="label" + hidden).
                Keep input in the hit target with opacity:0 so the change event fires after Browse.
              */}
              <Box
                component="label"
                sx={{
                  position: 'relative',
                  display: 'block',
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                <input
                  ref={uploadFileInputRef}
                  type="file"
                  accept="application/pdf,.pdf,application/msword,.doc,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx,image/*,*/*"
                  onChange={handleFileUpload}
                  onClick={() => {
                    pickingUploadFileRef.current = true;
                  }}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer',
                    zIndex: 2
                  }}
                />
                <Button
                  variant="outlined"
                  startIcon={<AttachIcon />}
                  fullWidth
                  component="span"
                  sx={{ py: 2, pointerEvents: 'none' }}
                >
                  {uploadData.file ? 'Change file' : 'Choose File'}
                </Button>
              </Box>
              {uploadData.file ? (
                <Alert severity="success" sx={{ mt: 1 }}>
                  Selected: <strong>{uploadData.file.name}</strong>
                  {uploadData.file.size
                    ? ` (${Math.max(1, Math.round(uploadData.file.size / 1024))} KB)`
                    : ''}
                </Alert>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  PDF preferred for lease e-sign. After you pick a file, its name appears here.
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setUploadDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={uploadDocument} 
            variant="contained" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <UploadIcon />}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            {loading ? 'Uploading...' : 'Upload Document'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={attachConfirmOpen} onClose={() => setAttachConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Continue without attach?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            {ATTACH_WARN_MESSAGE}
          </Alert>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Upload anyway without a property or tenant? You can attach later by editing the document when that is available.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAttachConfirmOpen(false)}>Go back</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={performUpload}
            disabled={loading}
          >
            {loading ? 'Uploading...' : 'Upload unattached'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, doc: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete document?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            This removes the document from Documents
            {deleteConfirm.doc?.fileName ? ' and its stored file' : ''}. Pending signature invites
            for this file are not auto-cancelled — cancel those on the Signatures tab if needed.
          </Alert>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Delete <strong>{deleteConfirm.doc?.title || 'this document'}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, doc: null })}>Keep</Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDeleteDocument}
            disabled={loading}
          >
            {loading ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkDeleteConfirmOpen}
        onClose={() => setBulkDeleteConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete selected documents?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            Permanently deletes {selectedDocIds.length} document
            {selectedDocIds.length === 1 ? '' : 's'} and their stored files. Signature invites are
            not auto-cancelled — use Cancel all pending on the Signatures tab if needed.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteConfirmOpen(false)}>Keep</Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmBulkDeleteDocuments}
            disabled={loading}
          >
            {loading ? 'Deleting…' : `Delete ${selectedDocIds.length}`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelConfirm.open}
        onClose={() => setCancelConfirm({ open: false, request: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel signature request?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Cancel pending invite for{' '}
            <strong>{cancelConfirm.request?.documentTitle || cancelConfirm.request?.id}</strong>?
            Signers will no longer be able to complete that link.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelConfirm({ open: false, request: null })}>Keep</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => handleCancelSignatureRequest(cancelConfirm.request)}
            disabled={loading}
          >
            {loading ? 'Cancelling…' : 'Cancel request'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelAllConfirmOpen}
        onClose={() => setCancelAllConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel all pending invites?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            This cancels {pendingSignatureCount} pending/sent signature request
            {pendingSignatureCount === 1 ? '' : 's'}. Useful for clearing test clutter.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelAllConfirmOpen(false)}>Keep</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleCancelAllPending}
            disabled={loading}
          >
            {loading ? 'Cancelling…' : 'Cancel all pending'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Dialog */}
      <Dialog 
        open={templateDialog} 
        onClose={() => {
          setTemplateDialog(false);
          setEditingTemplate(null);
        }} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          {editingTemplate ? 'Edit Template' : 'Create Template'}
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: { xs: 0.5, sm: 1 } }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Template Name"
                value={templateData.name}
                onChange={(e) => setTemplateData({ ...templateData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={templateData.category}
                  onChange={(e) => setTemplateData({ ...templateData, category: e.target.value })}
                  label="Category"
                >
                  <MenuItem value="Lease">Lease</MenuItem>
                  <MenuItem value="Contract">Contract</MenuItem>
                  <MenuItem value="Form">Form</MenuItem>
                  <MenuItem value="Letter">Letter</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={templateData.description}
                onChange={(e) => setTemplateData({ ...templateData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Template Content"
                multiline
                rows={8}
                value={templateData.content}
                onChange={(e) => setTemplateData({ ...templateData, content: e.target.value })}
                placeholder="Enter your template content here..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => {
              setTemplateDialog(false);
              setEditingTemplate(null);
            }}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={editingTemplate ? updateTemplate : createTemplate} 
            variant="contained" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            {loading ? (editingTemplate ? 'Updating...' : 'Creating...') : (editingTemplate ? 'Update Template' : 'Create Template')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Document Dialog */}
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {selectedDocument?.title}
        </DialogTitle>
        <DialogContent>
          {selectedDocument && (
            <Box>
              {esignFieldCount(selectedDocument) > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  This document has {esignFieldCount(selectedDocument)} saved e-sign field
                  {esignFieldCount(selectedDocument) === 1 ? '' : 's'}. PDF Preview shows the
                  original file only — use <strong>Edit fields</strong> to add merge boxes or move
                  overlays.
                  <Box sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<DrawIcon />}
                      onClick={() => {
                        setViewDialog(false);
                        openPlaceFields(selectedDocument);
                      }}
                    >
                      Edit fields
                    </Button>
                  </Box>
                </Alert>
              )}
              {esignFieldCount(selectedDocument) === 0 &&
                (selectedDocument.fileUrl ||
                  selectedDocument.fileName ||
                  selectedDocument.storagePath ||
                  selectedDocument.id) && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No e-sign fields yet — tap Place fields to add signature and merge boxes.
                    <Box sx={{ mt: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<DrawIcon />}
                        onClick={() => {
                          setViewDialog(false);
                          openPlaceFields(selectedDocument);
                        }}
                      >
                        Place fields
                      </Button>
                    </Box>
                  </Alert>
                )}
              
              {/* PDF Viewer */}
              {(selectedDocument.type === 'pdf' || selectedDocument.fileUrl?.includes('.pdf')) && selectedDocument.fileUrl && (
                <Box sx={{ mb: 3, border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
                  <Box sx={{ p: 2, bgcolor: 'grey.100', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2">PDF Preview</Typography>
                    <Button 
                      size="small" 
                      variant="outlined"
                      onClick={() => openDocumentFile(selectedDocument)}
                    >
                      Open in New Tab
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      height: { xs: '420px', sm: '560px' },
                      bgcolor: 'grey.50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {previewLoading && <CircularProgress size={28} />}
                    {!previewLoading && previewError && (
                      <Box sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="body2" color="error" gutterBottom>
                          {previewError}
                        </Typography>
                        <Button variant="contained" onClick={() => openDocumentFile(selectedDocument)}>
                          Open PDF
                        </Button>
                      </Box>
                    )}
                    {!previewLoading && !previewError && previewBlobUrl && (
                      <Box
                        component="iframe"
                        title={`Preview ${selectedDocument.title || 'document'}`}
                        src={previewBlobUrl}
                        sx={{ width: '100%', height: '100%', border: 0 }}
                      />
                    )}
                    {!previewLoading && !previewError && !previewBlobUrl && (
                      <Button variant="contained" onClick={() => openDocumentFile(selectedDocument)}>
                        View PDF
                      </Button>
                    )}
                  </Box>
                </Box>
              )}
              
              {/* Fallback for documents without file URL */}
              {(selectedDocument.type === 'pdf' || selectedDocument.fileUrl?.includes('.pdf')) && !selectedDocument.fileUrl && (
                <Box sx={{ mb: 3, p: 3, border: '1px solid #ddd', borderRadius: 1, textAlign: 'center' }}>
                  <Typography variant="h6" color="textSecondary" gutterBottom>
                    PDF Preview Not Available
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    This document was uploaded before the preview feature was added.
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    To enable preview, please re-upload the PDF file.
                  </Typography>
                </Box>
              )}
              
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>Document Details</Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Category:</strong> {selectedDocument.category}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Status:</strong> {selectedDocument.status}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Size:</strong> {selectedDocument.size}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Version:</strong> {selectedDocument.version}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Uploaded:</strong> {selectedDocument.uploadedDate}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>Signatures</Typography>
                  {selectedDocument.signatures && selectedDocument.signatures.length > 0 ? (
                    <List dense>
                      {selectedDocument.signatures.map((signature, index) => (
                        <ListItem key={index}>
                          <ListItemAvatar>
                            <Avatar>
                              <PersonIcon />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={signature.name}
                            secondary={`${signature.role} • ${signature.date}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No signatures yet
                    </Typography>
                  )}
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Tags</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedDocument.tags && selectedDocument.tags.map((tag, index) => (
                    <Chip key={index} label={tag} size="small" />
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(false)}>Close</Button>
          <Button 
            variant="contained" 
            startIcon={<DownloadIcon />}
            onClick={() => {
              if (selectedDocument.fileUrl || selectedDocument.id) {
                openDocumentFile(selectedDocument, { download: true });
              } else {
                setSnackbar({ open: true, message: 'File not available for download', severity: 'warning' });
              }
            }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sign Document Dialog */}
      <Dialog open={signDialog} onClose={() => setSignDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Sign Document</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Signer Name"
                value={signatureData.signerName}
                onChange={(e) => setSignatureData({ ...signatureData, signerName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Signer Email"
                type="email"
                value={signatureData.signerEmail}
                onChange={(e) => setSignatureData({ ...signatureData, signerEmail: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Digital Signature"
                multiline
                rows={3}
                value={signatureData.signature}
                onChange={(e) => setSignatureData({ ...signatureData, signature: e.target.value })}
                placeholder="Type your name or draw your signature..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSignDialog(false)}>Cancel</Button>
          <Button 
            onClick={signDocument} 
            variant="contained" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SecurityIcon />}
          >
            {loading ? 'Signing...' : 'Sign Document'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Document Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Document</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
            {ATTACH_WARN_MESSAGE}
          </Alert>
          {!editData.propertyId && !editData.tenantId && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              This file is unattached — pick a property and/or tenant below.
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Document Title"
                value={editData.title}
                onChange={(e) => setEditData({ ...editData, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={editData.description}
                onChange={(e) => setEditData({ ...editData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Property</InputLabel>
                <Select
                  value={editData.propertyId}
                  onChange={(e) => setEditData({ ...editData, propertyId: e.target.value })}
                  label="Property"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {properties.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name || p.address || p.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tenant</InputLabel>
                <Select
                  value={editData.tenantId}
                  onChange={(e) => setEditData({ ...editData, tenantId: e.target.value })}
                  label="Tenant"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {tenants.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {[t.firstName, t.lastName].filter(Boolean).join(' ') || t.email || t.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={editData.category}
                  onChange={(e) => setEditData({ ...editData, category: e.target.value })}
                  label="Category"
                >
                  <MenuItem value="Lease">Lease</MenuItem>
                  <MenuItem value="Contract">Contract</MenuItem>
                  <MenuItem value="Permit">Permit</MenuItem>
                  <MenuItem value="Insurance">Insurance</MenuItem>
                  <MenuItem value="Form">Form</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={editData.status}
                  onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                  label="Status"
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="signed">Signed</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tags"
                value={editData.tags.join(', ')}
                onChange={(e) => setEditData({ ...editData, tags: e.target.value.split(', ') })}
                placeholder="lease, residential, signed"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Expiry Date"
                type="date"
                value={editData.expiryDate}
                onChange={(e) => setEditData({ ...editData, expiryDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button 
            onClick={editDocument} 
            variant="contained" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <EditIcon />}
          >
            {loading ? 'Updating...' : 'Update Document'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Google Doc Dialog (Documents hub) */}
      <Dialog
        open={googleDocDialog}
        onClose={() => !loading && setGoogleDocDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Google Doc</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
            {ATTACH_WARN_MESSAGE} Creates in Drive and should appear in this Documents hub.
          </Alert>
          {!googleDocData.propertyId && !googleDocData.tenantId && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              No property or tenant selected — you will be asked to confirm before creating.
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Document Title"
                value={googleDocData.title}
                onChange={(e) => setGoogleDocData({ ...googleDocData, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Property</InputLabel>
                <Select
                  value={googleDocData.propertyId}
                  onChange={(e) => setGoogleDocData({ ...googleDocData, propertyId: e.target.value })}
                  label="Property"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {properties.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name || p.address || p.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tenant</InputLabel>
                <Select
                  value={googleDocData.tenantId}
                  onChange={(e) => setGoogleDocData({ ...googleDocData, tenantId: e.target.value })}
                  label="Tenant"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {tenants.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {[t.firstName, t.lastName].filter(Boolean).join(' ') || t.email || t.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Drive folder</InputLabel>
                <Select
                  value={googleDocData.folderId}
                  onChange={(e) => setGoogleDocData({ ...googleDocData, folderId: e.target.value })}
                  label="Drive folder"
                >
                  <MenuItem value="">
                    <em>Root folder</em>
                  </MenuItem>
                  {googleFolders.map((folder) => (
                    <MenuItem key={folder.id} value={folder.id}>
                      {folder.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGoogleDocDialog(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={createGoogleDoc}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <AttachIcon />}
          >
            {loading ? 'Creating...' : 'Create Google Doc'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={googleDocAttachConfirmOpen}
        onClose={() => setGoogleDocAttachConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Continue without attach?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            {ATTACH_WARN_MESSAGE}
          </Alert>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Create the Google Doc without a property or tenant? You can attach later from Edit on the hub row.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGoogleDocAttachConfirmOpen(false)}>Go back</Button>
          <Button
            onClick={performCreateGoogleDoc}
            variant="contained"
            color="warning"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create unattached'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Signature Request Dialog */}
      <Dialog
        open={signatureRequestDialog}
        onClose={() => setSignatureRequestDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
      >
        <DialogTitle>Request Document Signatures</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Document</InputLabel>
                <Select
                  value={signatureRequestData.documentId}
                  onChange={(e) =>
                    setSignatureRequestData((prev) => ({
                      ...prev,
                      documentId: e.target.value
                    }))
                  }
                  label="Document"
                >
                  {documents.map((doc) => (
                    <MenuItem key={doc.id} value={doc.id}>
                      {doc.title || doc.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Message to Signers"
                multiline
                rows={3}
                value={signatureRequestData.message}
                onChange={(e) =>
                  setSignatureRequestData((prev) => ({ ...prev, message: e.target.value }))
                }
                placeholder="Please review and sign this document..."
              />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 0 }}>
                {Array.isArray(signatureRequestData.fields) && signatureRequestData.fields.length > 0
                  ? `${signatureRequestData.fields.length} placed field(s) will be sent with this request (BYO PDF overlay).`
                  : 'Open Edit fields to place signature/merge boxes on the PDF before sending.'}
              </Alert>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Attach property/tenant/lease so merge fields can fill. You can reopen Edit fields any
                time to add more merge boxes.
              </Typography>
              {signatureRequestData.documentId &&
                documents.find((d) => d.id === signatureRequestData.documentId) && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<DrawIcon />}
                    sx={{ mt: 1 }}
                    onClick={() => {
                      const d = documents.find((doc) => doc.id === signatureRequestData.documentId);
                      if (d) openPlaceFields(d);
                      setSignatureRequestDialog(false);
                    }}
                  >
                    {esignFieldCount(
                      documents.find((d) => d.id === signatureRequestData.documentId)
                    ) > 0
                      ? 'Edit fields (add merge / move boxes)'
                      : 'Place fields on PDF'}
                  </Button>
                )}
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                  gap: 1,
                  flexWrap: 'wrap'
                }}
              >
                <Typography variant="subtitle2">
                  Signers ({(signatureRequestData.signers || []).length})
                </Typography>
                <Button
                  type="button"
                  size="small"
                  variant="outlined"
                  onClick={addSigner}
                  startIcon={<AddIcon />}
                >
                  Add Signer
                </Button>
              </Box>
              {(signatureRequestData.signers || []).length === 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No signers yet — tap Add Signer, then enter name + email.
                </Alert>
              )}
              <Alert severity="info" sx={{ mb: 2 }}>
                Use distinct roles for co-signers (Tenant + Tenant 2). Merge fields always fill from
                the document’s attached tenant — not from these rows.
              </Alert>
              {(Array.isArray(signatureRequestData.signers) ? signatureRequestData.signers : []).map(
                (signer, index) => {
                  const roleValue = signer.role || 'Tenant';
                  const roleOptions = SIGNER_ROLE_OPTIONS.includes(roleValue)
                    ? SIGNER_ROLE_OPTIONS
                    : [...SIGNER_ROLE_OPTIONS, roleValue];
                  return (
                    <Box
                      key={`signer-${index}`}
                      sx={{
                        display: 'flex',
                        gap: 1.5,
                        mb: 2,
                        alignItems: 'flex-start',
                        flexDirection: { xs: 'column', sm: 'row' },
                        p: { xs: 1.5, sm: 0 },
                        border: { xs: '1px solid', sm: 'none' },
                        borderColor: { xs: 'divider', sm: 'transparent' },
                        borderRadius: 1
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: { xs: 'block', sm: 'none' } }}
                      >
                        Signer {index + 1}
                      </Typography>
                      <TextField
                        label="Name"
                        value={signer.name || ''}
                        onChange={(e) => updateSigner(index, 'name', e.target.value)}
                        size="small"
                        fullWidth
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        label="Email"
                        type="email"
                        value={signer.email || ''}
                        onChange={(e) => updateSigner(index, 'email', e.target.value)}
                        size="small"
                        fullWidth
                        sx={{ flex: 1 }}
                      />
                      <FormControl size="small" fullWidth sx={{ minWidth: { sm: 130 }, maxWidth: { sm: 160 } }}>
                        <InputLabel>Role</InputLabel>
                        <Select
                          value={roleValue}
                          onChange={(e) => updateSigner(index, 'role', e.target.value)}
                          label="Role"
                        >
                          {roleOptions.map((role) => (
                            <MenuItem key={role} value={role}>
                              {role}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <IconButton
                        size="small"
                        aria-label={`Remove signer ${index + 1}`}
                        onClick={() => removeSigner(index)}
                        sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  );
                }
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2, py: 1.5 }}>
          <Button onClick={() => setSignatureRequestDialog(false)}>Cancel</Button>
          <Button
            type="button"
            onClick={addSigner}
            variant="outlined"
            startIcon={<AddIcon />}
          >
            Add Signer
          </Button>
          <Button 
            onClick={createSignatureRequest} 
            variant="contained" 
            disabled={
              loading ||
              !(signatureRequestData.signers || []).length ||
              !signatureRequestData.documentId
            }
            startIcon={loading ? <CircularProgress size={20} /> : <SecurityIcon />}
          >
            {loading ? 'Creating...' : 'Create Signature Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Google Drive Sync Dialog */}
      <Dialog open={syncDialog} onClose={() => setSyncDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Sync Document with Google Drive</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Document: {selectedDocumentForSync?.title}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Select Google Drive Folder</InputLabel>
                <Select
                  value={selectedFolder}
                  onChange={(e) => setSelectedFolder(e.target.value)}
                  label="Select Google Drive Folder"
                >
                  <MenuItem value="">
                    <em>Root Folder</em>
                  </MenuItem>
                  {googleFolders.map((folder) => (
                    <MenuItem key={folder.id} value={folder.id}>
                      {folder.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                This will upload the document to Google Drive and create a link between the local document and the Google Drive file.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDialog(false)}>Cancel</Button>
          <Button 
            onClick={syncDocumentWithGoogleDrive} 
            variant="contained" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <AttachIcon />}
          >
            {loading ? 'Syncing...' : 'Sync with Google Drive'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Signature Request Dialog */}
      <Dialog open={viewSignatureDialog} onClose={() => setViewSignatureDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Signature Request Details</DialogTitle>
        <DialogContent>
          {selectedSignatureRequest && (
            <Box>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>Request Information</Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Request ID:</strong> {selectedSignatureRequest.id}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Document:</strong> {selectedSignatureRequest.documentTitle}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Status:</strong> 
                    <Chip 
                      label={selectedSignatureRequest.status} 
                      color={getSignatureStatusColor(selectedSignatureRequest.status)} 
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Created:</strong> {selectedSignatureRequest.createdDate}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Expires:</strong> {selectedSignatureRequest.expiryDate}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>Signing Progress</Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      {selectedSignatureRequest.signers.filter(s => s.status === 'signed').length} of {selectedSignatureRequest.signers.length} signed
                    </Typography>
                    <Box sx={{ width: '100%', bgcolor: 'grey.200', borderRadius: 1, height: 8 }}>
                      <Box 
                        sx={{ 
                          width: `${(selectedSignatureRequest.signers.filter(s => s.status === 'signed').length / selectedSignatureRequest.signers.length) * 100}%`,
                          bgcolor: selectedSignatureRequest.status === 'completed' ? 'success.main' : 'primary.main',
                          height: 8,
                          borderRadius: 1
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="subtitle2" gutterBottom>Signers</Typography>
              <List>
                {selectedSignatureRequest.signers.map((signer, index) => (
                  <ListItem key={index}>
                    <ListItemAvatar>
                      <Avatar>
                        <PersonIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={signer.name}
                      secondary={signer.email}
                    />
                    <Box sx={{ ml: 2 }}>
                      {signer.status === 'signed' ? (
                        <Chip label="Signed" color="success" size="small" />
                      ) : signer.status === 'pending' ? (
                        <Chip label="Pending" color="warning" size="small" />
                      ) : (
                        <Chip label="Declined" color="error" size="small" />
                      )}
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewSignatureDialog(false)}>Close</Button>
          {selectedSignatureRequest?.status === 'completed' && (
            <Button 
              variant="contained" 
              startIcon={<DownloadIcon />}
              onClick={() => handleDownloadSignedDocument(selectedSignatureRequest)}
            >
              Download Signed Document
            </Button>
          )}
          {selectedSignatureRequest?.status === 'pending' && selectedSignatureRequest?.signingUrl && (
            <Button
              variant="outlined"
              onClick={() => copySigningLink(selectedSignatureRequest.signingUrl)}
            >
              Copy signing link
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <PdfFieldPlacer
        open={fieldPlacerOpen}
        onClose={() => setFieldPlacerOpen(false)}
        document={fieldPlacerDoc}
        signers={signatureRequestData.signers}
        tenants={tenants}
        onSignersChange={(next) => {
          setSignatureRequestData((prev) => ({
            ...prev,
            signers: next.length ? next : prev.signers
          }));
        }}
        onSaved={(fields) => {
          setSignatureRequestData((prev) => ({
            ...prev,
            documentId: fieldPlacerDoc?.id || prev.documentId,
            fields
          }));
          fetchDocuments();
          setSnackbar({
            open: true,
            message: `Saved ${fields.length} field(s) for e-sign`,
            severity: 'success'
          });
        }}
        onRequestSignatures={(fields) => {
          setSignatureRequestData((prev) => ({
            ...prev,
            documentId: fieldPlacerDoc?.id || prev.documentId,
            fields,
            signers:
              Array.isArray(prev.signers) && prev.signers.length
                ? prev.signers
                : [{ name: '', email: '', role: 'Tenant' }]
          }));
          setFieldPlacerOpen(false);
          setSignatureRequestDialog(true);
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DocumentManagement; 